// frontend/src/services/dataService.ts
import axios from 'axios'; // Import axios para usar isAxiosError
import axiosInstance from '../config/axiosInstance';

import {

    ProcessingStatus,
    DataType, // Asegúrate que DataType esté en src/tytpes/index.ts (exportado desde dataQuality o processingStaus)
    QualityRuleItem,
    SaveQualityRulesPayload,
    SaveQualityRulesResponse,
    FileType,
    HistoricalCostRecord,
    HistoricalCostUpdatePayload,
    HistoricalCostCreatePayload,
    TransactionDateRange,
    CostCoverageRecord,
    CostCompletenessRecord,
} from '../types'; // Importar tipos desde el index


// --- Función para obtener el Perfil de Datos (MODIFICADA v5.6.5) ---
// Ahora devuelve 'any' porque lee el snapshot JSON directamente del log
export const getDataProfile = async (dataType: DataType): Promise<any> => { // <-- Cambiado Promise<DataProfileResponse> a Promise<any>
    console.log(`[API Service] Fetching profile snapshot for: ${dataType}`);
    try {
        // Usa comillas invertidas (template literals)
        // Espera 'any' ya que la estructura es el JSON guardado
        const response = await axiosInstance.get<any>(`/data/profile`, {
            params: { file_type: dataType }
        });

        // Eliminamos la validación estricta de 'column_profiles' aquí,
        // ya que el backend devuelve el diccionario directamente.
        // El componente que usa los datos verificará la estructura si es necesario.
        if (!response || response.data === undefined || response.data === null) { // Chequeo más básico
            console.error("Invalid or empty API response (profile):", response);
            throw new Error(`Invalid or empty profile data received for ${dataType}`);
        }
        console.log(`[API Service] Profile snapshot for ${dataType} fetched successfully.`);
        return response.data; // Devuelve el objeto/diccionario recibido directamente
    } catch (err: any) {
        console.error(`[API Service] Error fetching profile for ${dataType}:`, err);
        // Mantener el relanzamiento del error detallado
        const errorDetail = err.response?.data?.detail || err.message || `Failed to load profile for ${dataType}`;
        // Importante relanzar para que useQuery/useQueries lo marquen como error
        throw new Error(errorDetail);
    }
};

// --- Función para obtener el Estado del Procesamiento (CORREGIDA FINAL) ---
// Acepta un solo fileType y devuelve la interfaz ProcessingStatus
export const getDataStatus = async (fileType: DataType): Promise<ProcessingStatus | null> => {
    console.log(`[API Service] Fetching status for type: ${fileType}`);
    try {
        const response = await axiosInstance.get<ProcessingStatus>(`/data/status`, {
            params: { file_type: fileType }
        });
        console.log(`[API Service] Status for ${fileType} fetched successfully:`, response.data);
        return response.data;

    } catch (err: any) {
         if (axios.isAxiosError(err) && err.response?.status === 404) {
            // Estrategia del Operador: Tratar 404 como un estado de datos (nulo), no un error.
            console.log(`[API Service] No status found for ${fileType} (404), returning null.`);
            return null;
         }
         // Para cualquier otro error, relanzarlo para que React Query lo capture.
         console.error(`[API Service] Error fetching status for ${fileType}:`, err);
         throw err; // Relanzar el error original para que isError funcione correctamente.
    }
};

export const getSavedMapping = async (dataType: DataType): Promise<Record<string, string>> => {
    console.log(`[API Service] Fetching saved mapping for: ${dataType}`);
    try {
        const response = await axiosInstance.get<{ mapping: Record<string, string> }>(`/data/mapping/${dataType}`);
        
        if (response.data && typeof response.data.mapping === 'object') {
            console.log(`[API Service] Saved mapping for ${dataType} fetched successfully.`);
            return response.data.mapping; // Devuelve el diccionario de mapeo
        }
        
        // Esto es un fallback en caso de una respuesta malformada del backend
        console.error(`[API Service] Invalid mapping response structure for ${dataType}:`, response.data);
        throw new Error(`Invalid mapping data received for ${dataType}`);

    } catch (err: any) {
        // Un 404 aquí no es un error, el backend devuelve un mapeo vacío.
        // Solo los errores 500 u otros problemas de red deben ser relanzados.
        console.error(`[API Service] Error fetching saved mapping for ${dataType}:`, err);
        const errorDetail = err.response?.data?.detail || err.message || `Failed to load saved mapping for ${dataType}`;
        throw new Error(errorDetail);
    }
};

// --- NUEVA Función para obtener las Reglas de Calidad ---
export const getQualityRules = async (dataType: DataType): Promise<QualityRuleItem[]> => {
    console.log(`[API Service] Fetching quality rules for: ${dataType}`);
    try {
        const response = await axiosInstance.get<QualityRuleItem[]>(`/data/quality-rules`, {
            params: { data_type: dataType } // El backend espera data_type aquí
        });
        console.log(`[API Service] Quality rules for ${dataType} fetched successfully.`);
        // El backend devuelve una lista (vacía si no hay reglas), así que la devolvemos directamente
        return response.data;
    } catch (err: any) {
        console.error(`[API Service] Error fetching quality rules for ${dataType}:`, err);
        const errorDetail = err.response?.data?.detail || err.message || `Failed to load quality rules for ${dataType}`;
        throw new Error(errorDetail);
    }
};

// --- NUEVA Función para guardar las Reglas de Calidad ---
export const saveQualityRules = async (payload: SaveQualityRulesPayload): Promise<SaveQualityRulesResponse> => {
    console.log(`[API Service] Saving quality rules for: ${payload.data_type}`, payload.rules); // Usar payload.data_type
    try {
        const response = await axiosInstance.post<SaveQualityRulesResponse>(`/data/quality-rules`, payload);
        console.log(`[API Service] Quality rules for ${payload.data_type} saved successfully.`); // Usar payload.data_type
        return response.data;
    } catch (err: any) {
        console.error(`[API Service] Error saving quality rules for ${payload.data_type}:`, err); // Usar payload.data_type
        const errorDetail = err.response?.data?.detail || err.message || `Failed to save quality rules for ${payload.data_type}`; // Usar payload.data_type
        throw new Error(errorDetail);
    }
};

// --- NUEVA Función para aplicar reglas y reprocesar ---
export const applyRules = async (payload: { dataType: DataType }): Promise<any> => { // Recibe dataType (camelCase)
    console.log(`[API Service] Applying rules for: ${payload.dataType}`);

    // **** INICIO CAMBIO ****
    // Crear el payload que el backend ESPERA (con snake_case)
    const payloadToSend = {
        data_type: payload.dataType // Mapear de dataType (frontend) a data_type (backend)
    };
    // **** FIN CAMBIO ****

    try {
        // Llama al nuevo endpoint, enviando el payload TRANSFORMADO
        // Usa payloadToSend aquí en lugar de payload
        const response = await axiosInstance.post<any>(`/data/apply-rules`, payloadToSend);
        console.log(`[API Service] Rules applied for ${payload.dataType} successfully.`);
        return response.data; // Devuelve la respuesta del backend (ej: { message: ..., processed_rows: ... })
    } catch (err: any) {
        console.error(`[API Service] Error applying rules for ${payload.dataType}:`, err);
        const errorDetail = err.response?.data?.detail || err.message || `Failed to apply rules for ${payload.dataType}`;
        throw new Error(errorDetail);
    }
};

export const checkDataExists = async (fileType: FileType): Promise<boolean> => {
  try {
    // Usamos el mismo endpoint de 'status', pero solo nos importa si la llamada tiene éxito.
    await axiosInstance.get(`/data/status?file_type=${fileType}`);
    // Si la llamada NO lanza un error (es decir, devuelve 200 OK), significa que encontró un log.
    return true;
  } catch (error: any) {
    // Si la llamada lanza un error (especialmente un 404), significa que no hay datos.
    return false;
  }
};

// --- Funciones CRUD para Costes Históricos ---

export const getHistoricalCosts = async (page = 1, pageSize = 50): Promise<HistoricalCostRecord[]> => {
    console.log(`[API Service] Fetching historical costs for page ${page}`);
    try {
        const response = await axiosInstance.get<HistoricalCostRecord[]>('/data/historical-costs', {
            params: { page, page_size: pageSize }
        });
        return response.data;
    } catch (err: any) {
        console.error(`[API Service] Error fetching historical costs:`, err);
        const errorDetail = err.response?.data?.detail || err.message || 'Failed to load historical costs';
        throw new Error(errorDetail);
    }
};

export const updateHistoricalCost = async (recordId: string, payload: HistoricalCostUpdatePayload): Promise<HistoricalCostRecord> => {
    console.log(`[API Service] Updating historical cost record: ${recordId}`);
    try {
        const response = await axiosInstance.patch<HistoricalCostRecord>(`/data/historical-costs/${recordId}`, payload);
        return response.data;
    } catch (err: any) {
        console.error(`[API Service] Error updating historical cost ${recordId}:`, err);
        const errorDetail = err.response?.data?.detail || err.message || 'Failed to update cost record';
        throw new Error(errorDetail);
    }
};

export const deleteHistoricalCost = async (recordId: string): Promise<void> => {
    console.log(`[API Service] Deleting historical cost record: ${recordId}`);
    try {
        await axiosInstance.delete(`/data/historical-costs/${recordId}`);
    } catch (err: any) {
        console.error(`[API Service] Error deleting historical cost ${recordId}:`, err);
        const errorDetail = err.response?.data?.detail || err.message || 'Failed to delete cost record';
        throw new Error(errorDetail);
    }
};

export const createHistoricalCost = async (payload: HistoricalCostCreatePayload): Promise<HistoricalCostRecord> => {
    console.log(`[API Service] Creating new historical cost record.`);
    try {
        const response = await axiosInstance.post<HistoricalCostRecord>('/data/historical-costs', payload);
        return response.data;
    } catch (err: any) {
        console.error(`[API Service] Error creating historical cost:`, err);
        const errorDetail = err.response?.data?.detail || err.message || 'Failed to create cost record';
        throw new Error(errorDetail);
    }
};

export const getTransactionDateRange = async (): Promise<TransactionDateRange> => {
    console.log(`[API Service] Fetching transaction date range.`);
    try {
        const response = await axiosInstance.get<TransactionDateRange>('/data/transactions/date-range');
        return response.data;
    } catch (err: any) {
        console.error(`[API Service] Error fetching transaction date range:`, err);
        const errorDetail = err.response?.data?.detail || err.message || 'Failed to load transaction date range';
        throw new Error(errorDetail);
    }
};

export const getCostCoverage = async (frequency: 'yearly' | 'quarterly' | 'monthly'): Promise<CostCoverageRecord[]> => {
    const { data } = await axiosInstance.get('/data/historical-costs/coverage', {
        params: { frequency },
    });
    return data;
};

export const getCostCompleteness = async (): Promise<CostCompletenessRecord[]> => {
    const { data } = await axiosInstance.get('/data/historical-costs/completeness');
    return data;
};

// --- NUEVA Función para obtener el conteo total de transacciones ---
export const getTransactionCount = async (): Promise<{ total_count: number }> => {
    console.log(`[API Service] Fetching total transaction count.`);
    try {
        const response = await axiosInstance.get<{ total_count: number }>(`/data/transactions/count`);
        console.log(`[API Service] Total transaction count fetched successfully:`, response.data);
        return response.data;

    } catch (err: any) {
        console.error(`[API Service] Error fetching total transaction count:`, err);
        const errorDetail = err.response?.data?.detail || err.message || `Failed to load total transaction count`;
        throw new Error(errorDetail);
    }
};